import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import billingApi from '@/services/billingApi';
import { useSubscription } from '@/hooks/useSubscription';

const STATUS_STYLES: Record<string, string> = {
  trial: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  past_due: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  expired: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const usageRows: { label: string; usageKey: string; limitKey: string }[] = [
  { label: 'Video translations', usageKey: 'videoTranslations', limitKey: 'videoTranslationsPerMonth' },
  { label: 'Audio translations', usageKey: 'audioTranslations', limitKey: 'audioTranslationsPerMonth' },
  { label: 'Speech-to-text minutes', usageKey: 'speechToTextMinutes', limitKey: 'speechToTextMinutesPerMonth' },
  { label: 'Text-to-speech characters', usageKey: 'textToSpeechCharacters', limitKey: 'textToSpeechCharactersPerMonth' },
  { label: 'AI clips', usageKey: 'clips', limitKey: 'clipsPerMonth' },
  { label: 'Connected accounts', usageKey: 'connectedAccounts', limitKey: 'connectedAccountsLimit' },
  { label: 'Projects', usageKey: 'projects', limitKey: 'maxProjects' },
  { label: 'Team members', usageKey: 'teamMembers', limitKey: 'maxTeamMembers' },
  { label: 'Voices', usageKey: 'voices', limitKey: 'maxVoices' },
  { label: 'Avatars', usageKey: 'avatars', limitKey: 'maxAvatars' },
];

const UsageBar: React.FC<{ label: string; used: number; limit: number }> = ({ label, used, limit }) => {
  const unlimited = limit < 0;
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(limit, 1)) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {used.toLocaleString()} {unlimited ? '' : `/ ${limit.toLocaleString()}`}
          {unlimited && ' (unlimited)'}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-blue-600'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
};

const BillingPage: React.FC = () => {
  const { data: subscription, isLoading } = useSubscription();
  const queryClient = useQueryClient();

  const portalMutation = useMutation({
    mutationFn: () => billingApi.createPortalSession(),
    onSuccess: (res) => {
      if (res.data.portalUrl) window.location.href = res.data.portalUrl;
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to open billing portal'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => billingApi.cancelSubscription(),
    onSuccess: () => {
      toast.success('Subscription will cancel at the end of the billing period');
      queryClient.invalidateQueries(['subscription']);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to cancel subscription'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => billingApi.reactivateSubscription(),
    onSuccess: () => {
      toast.success('Subscription reactivated');
      queryClient.invalidateQueries(['subscription']);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to reactivate subscription'),
  });

  if (isLoading || !subscription) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing</h1>
        <Link to="/app/pricing" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
          View plans
        </Link>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{subscription.planName}</p>
            {subscription.status === 'trial' && subscription.trialEndsAt && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Trial ends {new Date(subscription.trialEndsAt).toLocaleDateString()}
              </p>
            )}
            {subscription.currentPeriodEnd && subscription.status !== 'trial' && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {subscription.cancelAtPeriodEnd ? 'Access ends' : 'Renews'}{' '}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </p>
            )}
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[subscription.status] || STATUS_STYLES.inactive}`}>
            {subscription.status.replace('_', ' ')}
          </span>
        </div>

        {subscription.cancelAtPeriodEnd && (
          <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 text-sm">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            Your subscription is set to cancel at the end of the current billing period.
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isLoading}
            className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            {portalMutation.isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            Manage billing details
          </button>
          {subscription.cancelAtPeriodEnd ? (
            <button
              onClick={() => reactivateMutation.mutate()}
              disabled={reactivateMutation.isLoading}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              {reactivateMutation.isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              Reactivate subscription
            </button>
          ) : (
            subscription.status === 'active' && (
              <button
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg flex items-center gap-2"
              >
                {cancelMutation.isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Cancel subscription
              </button>
            )
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-blue-600" /> Usage this period
        </h2>
        <div className="space-y-4">
          {usageRows.map((row) => (
            <UsageBar
              key={row.usageKey}
              label={row.label}
              used={(subscription.usage as any)[row.usageKey] ?? 0}
              limit={(subscription.entitlements as any)[row.limitKey] ?? 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
